import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'urql';

import {
  type CompositionV2Filters,
  PAY_V2_QUERY,
  PositionsEntityKind,
} from '@revelio/data-access';

import type { UpliftMode } from '../IndividualPotentialPage';
import { applyGeoFilter } from '../utils/applyGeoFilter';
import type { AdjacentRole } from './useAdjacentRoles';
import type {
  GeoLevel,
  ResolvedProfileIds,
  RoleGranularity,
} from './useResolveProfileIds';
import {
  BUCKET_SIZE,
  buildDistributionFromHistogram,
  findPercentileValue,
} from './useSalaryDistribution';

export interface AdjacentRoleWithSalary extends AdjacentRole {
  targetSalary: number | null;
  salaryDelta: number | null;
  salaryDeltaPercent: number | null;
}

interface UseAdjacentRoleSalariesResult {
  enrichedRoles: AdjacentRoleWithSalary[];
  isLoading: boolean;
  progress: number;
}

const MAX_ROLES_TO_ENRICH = 7;

const ROLE_GRANULARITY_TO_PAY_ENTITY: Record<
  RoleGranularity,
  PositionsEntityKind
> = {
  role_k15000: PositionsEntityKind.RoleK15000,
  role_k1500: PositionsEntityKind.RoleK1500,
  role_k150: PositionsEntityKind.RoleK150,
  role_k7: PositionsEntityKind.RoleK7,
};

export const useAdjacentRoleSalaries = (
  adjacentRoles: AdjacentRole[],
  resolvedIds: ResolvedProfileIds,
  selectedGeo: GeoLevel | null,
  userSalary: number,
  /** Granularity of the adjacent role IDs (should match the transitions query level) */
  roleGranularity: RoleGranularity = 'role_k150',
  upliftMode: UpliftMode = 'median',
  userPercentile: number | null = null
): UseAdjacentRoleSalariesResult => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState<Map<number, number | null>>(new Map());
  const prevKeyRef = useRef<string>('');

  const rolesToEnrich = adjacentRoles.slice(0, MAX_ROLES_TO_ENRICH);

  // Reset when adjacent roles or uplift mode change
  const rolesKey = rolesToEnrich.map((r) => r.id).join(',');
  const compositeKey = `${rolesKey}|${upliftMode}|${userPercentile}`;
  useEffect(() => {
    if (compositeKey !== prevKeyRef.current) {
      prevKeyRef.current = compositeKey;
      setCurrentIndex(0);
      setResults(new Map());
    }
  }, [compositeKey]);

  const currentRole =
    currentIndex < rolesToEnrich.length
      ? rolesToEnrich[currentIndex]
      : undefined;

  const payVariables = useMemo(() => {
    if (!currentRole || currentIndex >= rolesToEnrich.length) {
      return undefined;
    }

    const filters: CompositionV2Filters = { raw: false };
    if (selectedGeo) {
      applyGeoFilter(filters, selectedGeo);
    }

    const entityType =
      ROLE_GRANULARITY_TO_PAY_ENTITY[roleGranularity] ??
      PositionsEntityKind.RoleK150;

    return {
      req: {
        entities: [
          {
            EntID: currentRole.id,
            EntType: entityType,
          },
        ],
        filters,
      },
    };
  }, [
    currentRole,
    currentIndex,
    rolesToEnrich.length,
    selectedGeo,
    roleGranularity,
  ]);

  const [{ data, fetching, error }] = useQuery({
    query: PAY_V2_QUERY,
    variables: payVariables ?? {
      req: { entities: [], filters: { raw: false } },
    },
    pause: !payVariables,
  });

  // When data arrives, record result and advance
  useEffect(() => {
    if (!currentRole || fetching || currentIndex >= rolesToEnrich.length) {
      return;
    }

    if (data || error) {
      let targetSalary: number | null = null;

      if (data && !error) {
        const entity = data.pay_v2?.entities?.[0];
        if (upliftMode === 'percentile' && userPercentile !== null) {
          const histogram = entity?.metrics?.histogram;
          const totalHistogram = histogram?.total;
          if (totalHistogram && totalHistogram.length > 0) {
            const distribution = buildDistributionFromHistogram(
              totalHistogram.map((c: number | null) => c ?? 0),
              BUCKET_SIZE
            );
            targetSalary = findPercentileValue(
              distribution,
              userPercentile / 100
            );
          }
        } else {
          const overtime = entity?.metrics?.overtime;
          const salaryTotal = overtime?.SalaryTotal;
          if (salaryTotal && salaryTotal.length > 0) {
            targetSalary = salaryTotal[salaryTotal.length - 1] ?? null;
          }
        }
      }

      setResults((prev) => {
        const next = new Map(prev);
        next.set(currentRole.id, targetSalary);
        return next;
      });
      setCurrentIndex((prev) => prev + 1);
    }
  }, [
    data,
    error,
    fetching,
    currentRole,
    currentIndex,
    rolesToEnrich.length,
    upliftMode,
    userPercentile,
  ]);

  const enrichedRoles = useMemo((): AdjacentRoleWithSalary[] => {
    return rolesToEnrich.map((role) => {
      const targetSalary = results.get(role.id) ?? null;

      let salaryDelta: number | null = null;
      let salaryDeltaPercent: number | null = null;

      if (targetSalary !== null && userSalary > 0) {
        salaryDelta = targetSalary - userSalary;
        salaryDeltaPercent = ((targetSalary - userSalary) / userSalary) * 100;
      }

      return {
        ...role,
        targetSalary,
        salaryDelta,
        salaryDeltaPercent,
      };
    });
  }, [rolesToEnrich, results, userSalary]);

  const isLoading =
    rolesToEnrich.length > 0 && currentIndex < rolesToEnrich.length;

  const progress =
    rolesToEnrich.length > 0 ? currentIndex / rolesToEnrich.length : 1;

  return {
    enrichedRoles,
    isLoading,
    progress,
  };
};
